import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const MemberAccess = () => {
    const { api } = useAuth();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState(null);

    useEffect(() => {
        fetchUserRole();
        fetchMembers();
    }, []);

    const fetchUserRole = async () => {
        try {
            const response = await api.get('/api/user-role/');
            console.log('MemberAccess: User role response:', response.data);
            setUserRole(response.data.role);
        } catch (error) {
            console.error('MemberAccess: Failed to fetch user role:', error);
        }
    };

    const fetchMembers = async () => {
        try {
            setLoading(true);
            console.log('MemberAccess: Fetching members from /api/admin/members/');
            const response = await api.get('/api/admin/members/');
            console.log('MemberAccess: Members response:', response.data);
            setMembers(response.data.members);
            setError(null);
        } catch (err) {
            console.error('MemberAccess: Members fetch error:', err);
            console.error('MemberAccess: Error response:', err.response?.data);
            console.error('MemberAccess: Error status:', err.response?.status);
            setError(`Failed to load members: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMember = async (userId, username) => {
        if (!window.confirm(`Are you sure you want to remove ${username} from the society?`)) {
            return;
        }

        try {
            await api.delete(`/api/admin/members/${userId}/delete/`);
            setMembers(members.filter(member => member.id !== userId));
            alert(`${username} has been removed from the society`);
        } catch (err) {
            alert(`Failed to remove member: ${err.response?.data?.error || err.message}`);
        }
    };

    const isAdmin = userRole === 'Admin' || userRole === 'superuser';

    // Debug: Log current state
    console.log('MemberAccess: Current state:', { userRole, isAdmin, loading, error, membersCount: members.length });

    // Redirect non-admin users
    if (!isAdmin) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
                    <p className="text-gray-600 mb-6">
                        You don't have permission to access this page. Only administrators can view member information.
                    </p>
                    <p className="text-sm text-gray-500 mb-4">Your role: {userRole || 'Unknown'}</p>
                    <Link
                        to="/"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                        Back to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="text-center p-8">Loading members...</div>;
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-100">
                <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <div className="text-center p-8">
                        <div className="text-red-600 mb-4">{error}</div>
                        <button
                            onClick={fetchMembers}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <Link to="/" className="text-sm text-indigo-600 hover:underline mb-2 block">
                        &larr; Back to Dashboard
                    </Link>
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Member Access</h1>
                            <p className="mt-2 text-gray-600">
                                Manage and view all registered members of your society
                            </p>
                        </div>
                        <div className="flex items-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Admin Only
                            </span>
                        </div>
                    </div>
                </div>

                {/* Statistics Card */}
                <div className="mb-6">
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                                        <span className="text-white text-sm font-medium">👥</span>
                                    </div>
                                </div>
                                <div className="ml-5 w-0 flex-1">
                                    <dl>
                                        <dt className="text-sm font-medium text-gray-500 truncate">Total Members</dt>
                                        <dd className="text-lg font-medium text-gray-900">{members.length}</dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Members List */}
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">Society Members</h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            View and manage all registered members
                        </p>
                    </div>
                    
                    {members.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                            <p className="text-gray-500">No members found.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {members.map((member) => (
                                        <tr key={member.id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10">
                                                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                                            <span className="text-sm font-medium text-gray-700">
                                                                {member.first_name?.[0] || member.username[0].toUpperCase()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {member.first_name} {member.last_name}
                                                        </div>
                                                        <div className="text-sm text-gray-500">{member.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                    member.role === 'Admin' 
                                                        ? 'bg-red-100 text-red-800' 
                                                        : 'bg-green-100 text-green-800'
                                                }`}>
                                                    {member.role}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(member.joined_date).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                    member.is_active 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {member.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => handleDeleteMember(member.id, member.username)}
                                                    className="text-red-600 hover:text-red-900"
                                                    disabled={member.role === 'Admin'}
                                                    title={member.role === 'Admin' ? 'Cannot delete admin accounts' : 'Remove member'}
                                                >
                                                    {member.role === 'Admin' ? 'Cannot Delete' : 'Remove'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Additional Actions */}
                <div className="mt-6 bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Link
                            to="/"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                            📊 Back to Dashboard
                        </Link>
                        <button
                            onClick={fetchMembers}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                            🔄 Refresh Members
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemberAccess; 